import React from "react";
import { isZh } from "../utils/language";

function countFiles(files = {}, type) {
  if (!files) return 0;
  return files[type]?.length || 0;
}

function previewText(text = "", max = 80) {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "..." : text;
}

function formatNotes(text = "", max = 100) {
  if (!text) return "";

  const trimmed = text.length > max
    ? text.slice(0, max) + "..."
    : text;

  // escape HTML dulu (penting untuk keamanan)
  const escaped = trimmed
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // convert newline → <br/>
  return escaped.replace(/\n/g, "<br/>");
}

export default function SongCard({ song, onPlay, isPlaying, onEdit, onManageFiles, onShare, hideMeta, mode }) {
    const zh = isZh();

    const labelNada = zh ? "調寄" : "Nada (Diaoji)";
    const labelLirik = zh ? "語寄" : "Lirik (Yuji)";

		const hasLirik = song.JudulLirik && song.JudulLirik.trim() !== "";

    const fileTypes = [
      { key: "Text", icon: "📄", labelZh: "文本", labelId: "Teks" },
      { key: "Slides", icon: "📊", labelZh: "簡報", labelId: "Slide" },
      { key: "Video", icon: "🎬", labelZh: "影片", labelId: "Video" },
      { key: "Audio", icon: "🎵", labelZh: "音頻", labelId: "Audio" },
      { key: "Other", icon: "📦", labelZh: "其他", labelId: "Lainnya" }
    ];

    const files = song.song_files || [];
    const hasAnyFile = files.length > 0;
    const latestVersionNotes = getLatestVersionNotes(files);

    const isDaftarMode = mode === "daftar";

    function countByType(files = [], type) {
      return files.filter(f => f.filetype === type).length;
    }

    function formatDateTime(dt) {
      if (!dt) return "";
      const d = new Date(dt);
      return d.toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }

    function getLatestVersionNotes(files = []) {
      if (!files.length) return "";

      // ambil yang ada notes
      const withNotes = files
        .filter(f => f.versionNotes)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

      return withNotes[0]?.versionNotes || "";
    }

    return (
      <div className="content-row" style={{ alignItems: "flex-start", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* PRIMARY TITLE */}
            <div style={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.4 }}>
              <span style={{
                fontSize: "0.7rem",
                color: "#999",
                marginRight: 6
              }}>
                {hasLirik ? labelLirik : labelNada}
              </span>

              {hasLirik
                ? `${song.JudulLirik || ""} ${song.JudulLirikPinyin || ""}`
                : `${song.JudulNada || ""} ${song.JudulNadaPinyin || ""}`}
            </div>

            {/* SECONDARY TITLE (only if lirik exists) */}
            {hasLirik && (
              <div style={{
                fontSize: "0.85rem",
                color: "#555",
                marginTop: 2
              }}>
                <span style={{
                  fontSize: "0.7rem",
                  color: "#aaa",
                  marginRight: 6
                }}>
                  {labelNada}
                </span>

                {song.JudulNada} {song.JudulNadaPinyin}
              </div>
            )}

            {/* Primary Language */}
            {song.primary_language && (
            <div style={{ fontSize: "0.75rem", color: "#888" }}>
                🌐 {song.primary_language}
            </div>
            )}

            {/* Notes preview */}
            {song.notes && (
              <div
                style={{ fontSize: "0.8rem", marginTop: 6, color: "#666", lineHeight: 1.4 }}
                dangerouslySetInnerHTML={{
                  __html: "📝 " + formatNotes(song.notes, 120)
                }}
              />
            )}

            {isDaftarMode && latestVersionNotes && (
              <div
                style={{
                  fontSize: "0.75rem",
                  marginTop: 4,
                  color: "#777",
                  lineHeight: 1.4
                }}
                dangerouslySetInnerHTML={{
                  __html: "📦 " + formatNotes(latestVersionNotes, 120)
                }}
              />
            )}

            {isDaftarMode && (song.updatedAt || song.recordEditor) && (
              <div style={{
                fontSize: "0.7rem",
                color: "#999",
                marginTop: "0.25rem"
              }}>
                ⏱ {formatDateTime(song.updatedAt)}
                {song.recordEditor && ` • ${song.recordEditor}`}
              </div>
            )}

          </div>
        </div>
        {/* BOTTOM BAR */}
        {!hideMeta && (
        <div style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "0rem"
        }}>

        {/* FILE COUNT */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>

          {!hasAnyFile && (
            <div style={{
              fontSize: "0.8rem",
              color: "#999",
              fontStyle: "italic"
            }}>
              {zh ? "尚無檔案" : "Belum ada lampiran"}
            </div>
          )}

          {hasAnyFile && fileTypes.map(t => {
            const count = countByType(files, t.key);

            if (!count) return null;

            return (
              <div
                key={t.key}
                title={`${t.labelZh} / ${t.labelId}: ${count}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: "0.8rem",
                  color: "#666",
                  cursor: "default"
                }}
              >
                <span>{t.icon}</span>
                <span>{count}</span>
              </div>
            );
          })}
        </div>

          {/* ACTIONS (RIGHT) */}
          <div style={{ display: "flex", gap: "0.2rem" }}>

            {/* MANAGE FILES */}
            {onManageFiles && (
              <button
                title={isZh() ? "管理檔案" : "Kelola File" }
                className="btn-secondary"
                onClick={() => onManageFiles(song)}
              >
                📁
              </button>
            )}

            {/* EDIT */}
            {onEdit && (
              <button
                title={isZh() ? "編輯" : "Edit" }
                className="btn-secondary"
                onClick={() => onEdit(song)}
              >
                ✏️
              </button>
            )}

            {onShare && (
              <button
                title={isZh() ? "分享" : "Share" }
                className="btn-secondary"
                onClick={() => onShare(song)}
              >
                🔗
              </button>
            )}

            {/* PLAY */}
            <button
              title={isZh() ? "播放預覽" : "Play Preview" }
              className="btn-icon"
              onClick={() => onPlay(song)}
            >
              {isPlaying ? "⏸" : "▶️"}
            </button>

          </div>
        </div>
        )}
      </div>
  );
}